import { ActionIcon, Box, Button, Group, Image, Modal, Stack, Tooltip } from "@mantine/core";
import { IconPhotoPlus, IconTrash } from "@tabler/icons-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

type ChecklistReportImagesEditorProps = {
  images?: string[];
  onSave: (oldValue: string[], newValue: string[]) => void;
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const getDisplayImageSrc = (src: string): string =>
  src.startsWith("data:") || src.startsWith("http") || src.startsWith("/api/image/") ? src : `/api/image/${src}`;

const ChecklistReportImagesEditor: React.FC<ChecklistReportImagesEditorProps> = ({ images = [], onSave }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [localImage, setLocalImage] = useState<string | null>(images[0] ?? null);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);

  useEffect(() => {
    setLocalImage(images[0] ?? null);
  }, [images]);

  const saveImage = useCallback(
    (newImage: string | null) => {
      const oldImages = localImage == null ? [] : [localImage];
      const newImages = newImage == null ? [] : [newImage];
      setLocalImage(newImage);
      onSave(oldImages, newImages);
    },
    [localImage, onSave],
  );

  const addFile = useCallback(
    async (files: FileList | File[]) => {
      const imageFile = Array.from(files).find((file) => file.type.startsWith("image/"));
      if (imageFile == null) {
        return;
      }

      saveImage(await readFileAsDataUrl(imageFile));
    },
    [saveImage],
  );

  const deleteImage = useCallback(() => {
    saveImage(null);
  }, [saveImage]);

  return (
    <Stack gap="xs">
      <input
        ref={inputRef}
        accept="image/*"
        onChange={(event) => {
          if (event.currentTarget.files != null) {
            void addFile(event.currentTarget.files);
          }
          event.currentTarget.value = "";
        }}
        style={{ display: "none" }}
        type="file"
      />
      <Button
        leftSection={<IconPhotoPlus size={16} />}
        onClick={() => inputRef.current?.click()}
        size="sm"
        variant="light"
      >
        {localImage == null ? "Legg til bilde" : "Bytt bilde"}
      </Button>

      {localImage != null && (
        <Group gap="xs">
          <Box pos="relative">
            <button
              aria-label="Åpne bilde"
              onClick={() => setSelectedImageSrc(getDisplayImageSrc(localImage))}
              style={{ background: "transparent", border: 0, cursor: "pointer", padding: 0 }}
              type="button"
            >
              <Image
                alt=""
                fit="cover"
                h={82}
                radius={4}
                src={getDisplayImageSrc(localImage)}
                style={{ border: "1px solid var(--mantine-color-gray-3)" }}
                w={110}
              />
            </button>
            <Tooltip label="Slett bilde" withArrow>
              <ActionIcon
                aria-label="Slett bilde"
                color="red"
                onClick={deleteImage}
                pos="absolute"
                right={4}
                size="sm"
                top={4}
                variant="filled"
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          </Box>
        </Group>
      )}

      <Modal
        opened={selectedImageSrc != null}
        onClose={() => setSelectedImageSrc(null)}
        title="Bilde"
        size="xl"
        centered
      >
        <Image alt="" fit="contain" mah="75vh" src={selectedImageSrc ?? ""} />
      </Modal>
    </Stack>
  );
};

export default ChecklistReportImagesEditor;
